// react
import { useEffect, useState } from 'react';

// third-party
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Button, CardContent, DialogTitle, Divider, InputLabel, Stack, TextField } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';

// project import
import RequiredAsterisk from 'components/RequiredAsterisk';
import { dispatch, useSelector } from 'store';
import { closeForm } from 'store/reducers/city';
import { openSnackbar } from '../../store/reducers/snackbar';
import { createCity, fetchAllCountries, updateCity } from './api';
import { ITimezoneOption, useTimezoneSelect } from 'react-timezone-select';
import AsyncSelectSearchFormField from 'components/async-select-search-form-field';
import FDCPServices from 'fdcp/fdcp-services';
import TournamentDto, { mapFdcpSeasonToTournamentDto } from 'common/dto-types/tournament-dto';
import CityDto, { mapFdcpCityToCityDto } from 'common/dto-types/city-dto';
import CountryDto from 'common/dto-types/country-dto';
import { timezones } from 'utils/calendar/timezone-switch';
import { READ_ONLY_FIELD_STYLES } from 'utils/field-styles';

type CityFormFields = {
    id: number;
    fdcpId: string;
    fdcpCity: CityDto | null;
    country: string;
    state: string;
    timeZone: ITimezoneOption;
    tournaments: TournamentDto[];
};

type CityFormProps = {
    refreshCallback: () => Promise<void>;
};

const findCountry = (countries: CountryDto[], country?: string | null): CountryDto | undefined =>
    countries.find(({ fifaCountryCode, name }) => country === fifaCountryCode || country === name);

const searchCityOptions = (inputValue: string): Promise<CityDto[]> =>
    FDCPServices.searchCityByName({ searchString: inputValue }).then((cities) => cities.map(mapFdcpCityToCityDto));

const searchTournamentOptions = (inputValue: string): Promise<TournamentDto[]> =>
    FDCPServices.searchSeasonByName({ searchString: inputValue }).then((seasons) =>
        seasons.map(mapFdcpSeasonToTournamentDto),
    );

const CityForm = ({ refreshCallback }: CityFormProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [countries, setCountries] = useState<CountryDto[]>([]);
    const data = useSelector((state) => state.city.data);
    const isCreating = !data;

    const { options: timeZoneOptions } = useTimezoneSelect({
        labelStyle: 'original',
        timezones: timezones,
        displayValue: 'UTC',
    });

    useEffect(() => {
        fetchAllCountries().then(setCountries);
    }, []);

    const convertToCityDto = (city: CityFormFields): CityDto => {
        const dto: CityDto = {
            id: city.id,
            fdcpId: city.fdcpId,
            name: city.fdcpCity?.name ?? '',
            country: findCountry(countries, city.country)?.name ?? city.country,
            state: city.state || null,
            timeZone: typeof city.timeZone?.value === 'string' ? city.timeZone.value : '',
            tournaments: city.tournaments,
        };

        return dto;
    };

    const initialValues: CityFormFields = {
        id: data?.id ?? 0,
        fdcpId: data?.fdcpId ?? '',
        fdcpCity: data ?? null,
        country: data?.country ?? '',
        state: data?.state ?? '',
        timeZone: data?.timeZone
            ? (timeZoneOptions.find((option) => option.value === data?.timeZone) as ITimezoneOption)
            : ({ value: '', label: '' } as ITimezoneOption),
        tournaments: data?.tournaments ?? [],
    };

    const validationSchema = yup.object({
        fdcpCity: yup.object().nullable().required('Name is required'),
        timeZone: yup.object().shape({ value: yup.string(), label: yup.string() }).required('Time zone is required'),
    });

    const handleCitySubmit = async (value: CityFormFields) => {
        setIsLoading(true);
        const dto = convertToCityDto(value);
        let response: Awaited<ReturnType<typeof createCity>>;
        let successMessage = '';
        let failedMessage = '';

        if (isCreating) {
            response = await createCity(dto);
            successMessage = 'Host City created successfully!';
            failedMessage = 'Failed to create Host City';
        } else {
            response = await updateCity(dto);
            successMessage = 'Host City updated successfully!';
            failedMessage = 'Failed to update Host City';
        }

        if (response?.status === 200) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: successMessage,
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            refreshCallback();
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: response?.data?.message ?? failedMessage,
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
        }
        setIsLoading(false);
    };

    const formik = useFormik<CityFormFields>({
        initialValues,
        validationSchema,
        onSubmit: handleCitySubmit,
    });

    const { dirty, isValid, getFieldProps, handleSubmit } = formik;

    const countryName = findCountry(countries, formik.values.country)?.name ?? formik.values.country;

    const handleCitySelected = (option: CityDto | CityDto[] | null) => {
        if (!option || Array.isArray(option)) return;

        formik.setFieldValue('fdcpId', option.fdcpId);
        formik.setFieldValue('country', option.country);
        formik.setFieldValue('state', option.state ?? '');
    };

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <DialogTitle>{isCreating ? 'Add' : 'Update'} City</DialogTitle>
                <CardContent>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <AsyncSelectSearchFormField
                                label="Name"
                                name="fdcpCity"
                                isRequired
                                initialValues={initialValues.fdcpCity}
                                fetchData={searchCityOptions}
                                getOptionLabel={(option) =>
                                    option.country ? `${option.name} — ${option.country}` : option.name
                                }
                                isOptionEqualToValue={(option, selectedValue) => option.fdcpId === selectedValue.fdcpId}
                                onChangeCallback={handleCitySelected}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>
                                FDCP ID <RequiredAsterisk />
                            </InputLabel>
                            <TextField
                                fullWidth
                                id="fdcpId"
                                placeholder="Selected automatically from Name"
                                disabled
                                InputProps={{ sx: READ_ONLY_FIELD_STYLES }}
                                {...getFieldProps('fdcpId')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>
                                Country <RequiredAsterisk />
                            </InputLabel>
                            <TextField
                                fullWidth
                                id="country"
                                placeholder="Selected automatically from Name"
                                disabled
                                InputProps={{ sx: READ_ONLY_FIELD_STYLES }}
                                {...getFieldProps('country')}
                                value={countryName}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>State</InputLabel>
                            <TextField
                                fullWidth
                                id="state"
                                placeholder={formik.values.fdcpCity ? '' : 'Filled automatically from Name'}
                                disabled
                                InputProps={{ sx: READ_ONLY_FIELD_STYLES }}
                                {...getFieldProps('state')}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <InputLabel>
                                Time Zone <RequiredAsterisk />
                            </InputLabel>
                            <Autocomplete
                                id="timeZone"
                                options={timeZoneOptions}
                                onChange={(_, newValue) => {
                                    formik.setFieldValue('timeZone', newValue);
                                }}
                                value={formik.values.timeZone}
                                getOptionLabel={(option) => option.label}
                                renderInput={(params) => <TextField {...params} />}
                                isOptionEqualToValue={(option, selectedValue) => {
                                    return option.value === selectedValue.value;
                                }}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <AsyncSelectSearchFormField
                                label="Tournaments"
                                name="tournaments"
                                initialValues={initialValues?.tournaments ?? []}
                                fetchData={searchTournamentOptions}
                                getOptionLabel={(option) => `${option.name} - [${option.fdcpId}]`}
                                isOptionEqualToValue={(option, selectedValue) => {
                                    return option.fdcpId === selectedValue.fdcpId;
                                }}
                                filterOptions={(x) => x}
                                multiple
                            />
                        </Stack>
                    </Stack>
                </CardContent>
                <Divider />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                    <Button color="error" size="small" disabled={isLoading} onClick={() => dispatch(closeForm())}>
                        Cancel
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={isLoading}
                        disabled={!isValid || (isCreating && !dirty)}
                    >
                        Save
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default CityForm;
